import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customInstance } from '../services/api-client';

export interface CatalogAssetProvenance {
  catalog_asset_id: number;
  connector_id: number;
  connector_name: string;
  provider_type: string;
  external_id: string;
  name: string;
  display_name?: string | null;
  description?: string | null;
  certification_status?: string | null;
  owners?: string[] | null;
  source_last_updated_at?: string | null;
  sync_status?: string | null;
}

export interface CatalogLinkSummary {
  link_id: number;
  catalog_asset: CatalogAssetProvenance;
  confidence_score: number;
  resolver_method: string;
  is_manual_fix: boolean;
}

export interface CatalogProviderSummary {
  connector_id?: number | null;
  connector_name: string;
  provider_type: string;
  status: string;
  is_healthy: boolean;
  asset_count: number;
  glossary_term_count: number;
  linked_warehouse_asset_count: number;
  last_synced_at?: string | null;
  writeback_supported: boolean;
}

export interface CatalogOverview {
  registry_available: boolean;
  total_warehouse_assets: number;
  linked_warehouse_assets: number;
  unlinked_warehouse_assets: number;
  catalog_assets: number;
  glossary_terms: number;
  pending_corrections: number;
  coverage_percentage: number;
  providers: CatalogProviderSummary[];
}

export interface CatalogWarehouseAsset {
  id: number;
  qualified_name: string;
  name?: string | null;
  table_external_id: string;
  dataset?: string | null;
  project?: string | null;
  warehouse_connector_id: number;
  warehouse_connector_name: string;
  warehouse_type: string;
  row_count_snapshot?: number | null;
  last_synced_at?: string | null;
  is_active: boolean;
  has_drift: boolean;
  source_description?: string | null;
  local_override_description?: string | null;
  effective_description?: string | null;
  column_count: number;
  pii_column_count: number;
  link_status: 'resolved' | 'missing' | 'ambiguous' | 'catalog_unavailable';
  link_status_reason: string;
  catalog_links: CatalogLinkSummary[];
  /** Table-level scheduled re-index toggle for Semantic Lookup. */
  auto_refresh_semantic_lookup: boolean;
}

export type SampleValuesStatus =
  | 'not_profiled'
  | 'indexed'
  | 'stale'
  | 'disabled'
  | 'profiling_failed';

export type SemanticLookupStatus =
  | 'not_indexed'
  | 'indexing'
  | 'indexed'
  | 'stale'
  | 'disabled'
  | 'index_failed';

export interface CatalogColumnDetail {
  id: number;
  name: string;
  data_type: string;
  source_description?: string | null;
  local_override_description?: string | null;
  effective_description?: string | null;
  effective_alias?: string | null;
  governed_aliases?: string[];
  /** DB-queried example values (never AI-generated), used to ground SQL literals. */
  sample_values?: string[];
  sample_values_status?: SampleValuesStatus | null;
  sample_values_profiled_at?: string | null;
  is_pii: boolean;
  is_hidden: boolean;
  link_status: 'resolved' | 'missing' | 'ambiguous' | 'catalog_unavailable';
  catalog_links: CatalogLinkSummary[];
  /** Semantic Lookup: per-column RAG index over the full distinct value space. */
  semantic_lookup_enabled: boolean;
  value_index_status?: SemanticLookupStatus | null;
  value_index_updated_at?: string | null;
  value_index_row_count?: number | null;
  semantic_lookup_eligible: boolean;
  semantic_lookup_ineligible_reason?: string | null;
}

export interface CatalogWarehouseAssetDetail extends CatalogWarehouseAsset {
  columns: CatalogColumnDetail[];
}

export interface CatalogWarehouseFacet {
  id: number;
  name: string;
  type: string;
  asset_count: number;
}

export interface CatalogLinkStatusFacet {
  status: CatalogWarehouseAsset['link_status'];
  count: number;
}

export interface CatalogWarehouseAssetFacets {
  warehouses: CatalogWarehouseFacet[];
  link_statuses: CatalogLinkStatusFacet[];
  pii_asset_count: number;
}

export interface CatalogWarehouseAssetPage {
  items: CatalogWarehouseAsset[];
  total: number;
  limit: number;
  offset: number;
  facets: CatalogWarehouseAssetFacets;
}

export interface CatalogWarehouseAssetPageParams {
  limit: number;
  offset: number;
  search?: string;
  warehouse_connector_id?: number;
  link_status?: CatalogWarehouseAsset['link_status'];
  has_pii?: boolean;
}

export interface CatalogAIColumnEnrichmentResult {
  column_id: number;
  name: string;
  description?: string | null;
  alias?: string | null;
  aliases?: string[];
  updated: boolean;
  skipped_reason?: string | null;
}

export interface CatalogAIEnrichmentResponse {
  status: string;
  table_id: number;
  mode?: 'missing' | 'table' | 'selected_columns' | 'all_columns' | null;
  table_description?: string | null;
  table_updated: boolean;
  columns: CatalogAIColumnEnrichmentResult[];
  skipped_reason?: string | null;
  prompt_key: string;
  model?: string | null;
  batch_size?: number | null;
  diagnostics?: Record<string, unknown> | null;
}

export interface CatalogAIEnrichmentRunResponse {
  id: number;
  table_id: number;
  status: 'pending' | 'running' | 'completed' | 'failed';
  force: boolean;
  mode?: 'missing' | 'table' | 'selected_columns' | 'all_columns' | null;
  prompt_key: string;
  model?: string | null;
  result?: CatalogAIEnrichmentResponse | null;
  error_message?: string | null;
  request_payload?: Record<string, unknown> | null;
  diagnostics?: Record<string, unknown> | null;
  celery_task_id?: string | null;
  created_at: string;
  started_at?: string | null;
  completed_at?: string | null;
}

export interface CatalogGlossaryTerm {
  id: number;
  name: string;
  description?: string | null;
  category?: string | null;
}

export interface CatalogCorrectionAsset {
  id: number;
  connector_id: number;
  external_id: string;
  provider_type: string;
  asset_type: string;
  name: string;
  display_name?: string | null;
  description?: string | null;
  certification_status?: string | null;
  owners?: string[] | null;
}

export interface CatalogCorrection {
  id: number;
  customer_id: string;
  user_id: number;
  catalog_asset_id: number;
  field_path: string;
  original_value?: string | null;
  proposed_value: string;
  reason?: string | null;
  source_metadata?: Record<string, unknown> | null;
  status: string;
  trace_id?: string | null;
  reviewer_id?: number | null;
  reviewer_notes?: string | null;
  reviewed_at?: string | null;
  created_at: string;
  catalog_asset?: CatalogCorrectionAsset | null;
}

export interface CatalogCorrectionCreate {
  catalog_asset_id: number;
  field_path: string;
  proposed_value: string;
  reason?: string;
  trace_id?: string;
}

export interface CatalogTableMetadataUpdate {
  local_override_description?: string | null;
  auto_refresh_semantic_lookup?: boolean;
}

export interface CatalogColumnMetadataUpdate {
  columnId: number;
  local_override_description?: string | null;
  effective_alias?: string | null;
  governed_aliases?: string[];
  sample_values_enabled?: boolean;
  semantic_lookup_enabled?: boolean;
}

export interface ProfileSampleValuesResponse {
  status: string;
  table_id: number;
  profiled: number;
  disabled: number;
  categorical_profiled?: number;
  sampled_rows?: number | null;
  error?: string | null;
  message?: string | null;
  queued: boolean;
  task_id?: string | null;
}

export interface ProfileConnectorSamplesResponse {
  status: string;
  config_id: number;
  queued: boolean;
  task_id?: string | null;
  error?: string | null;
  message?: string | null;
}

export interface IndexValuesResponse {
  status: string;
  table_id: number;
  indexed: number;
  skipped: number;
  failed: number;
  total_values_indexed: number;
  error?: string | null;
  message?: string | null;
  queued: boolean;
  task_id?: string | null;
}

export interface IndexConnectorValuesResponse {
  status: string;
  config_id: number;
  queued: boolean;
  task_id?: string | null;
  error?: string | null;
  message?: string | null;
}

export const catalogAppletKeys = {
  overview: ['catalog-applet', 'overview'] as const,
  assets: ['catalog-applet', 'assets'] as const,
  assetPage: (params: CatalogWarehouseAssetPageParams) => ['catalog-applet', 'assets', 'page', params] as const,
  asset: (id?: number) => ['catalog-applet', 'asset', id] as const,
  providers: ['catalog-applet', 'providers'] as const,
  glossary: ['catalog-applet', 'glossary'] as const,
  corrections: ['catalog-applet', 'corrections'] as const,
  aiEnrichmentRun: (id?: number | null) => ['catalog-applet', 'ai-enrichment-run', id] as const,
};

export function useCatalogOverview(enabled = true) {
  return useQuery({
    queryKey: catalogAppletKeys.overview,
    queryFn: () => customInstance<CatalogOverview>({ url: '/api/v1/catalog/overview', method: 'GET' }),
    enabled,
  });
}

export function useCatalogWarehouseAssets(enabled = true) {
  return useQuery({
    queryKey: catalogAppletKeys.assets,
    queryFn: () => customInstance<CatalogWarehouseAsset[]>({ url: '/api/v1/catalog/assets', method: 'GET' }),
    enabled,
  });
}

export function useCatalogWarehouseAssetPage(
  params: CatalogWarehouseAssetPageParams,
  enabled = true
) {
  return useQuery({
    queryKey: catalogAppletKeys.assetPage(params),
    queryFn: () =>
      customInstance<CatalogWarehouseAssetPage>({
        url: '/api/v1/catalog/assets/page',
        method: 'GET',
        params,
      }),
    enabled,
    staleTime: 30_000,
    gcTime: 60_000,
    placeholderData: (previousData) => previousData,
  });
}

export function useCatalogWarehouseAssetDetail(assetId?: number, enabled = true) {
  return useQuery({
    queryKey: catalogAppletKeys.asset(assetId),
    queryFn: () => customInstance<CatalogWarehouseAssetDetail>({ url: `/api/v1/catalog/assets/${assetId}`, method: 'GET' }),
    enabled: Boolean(assetId) && enabled,
  });
}

export function useEnrichCatalogWarehouseAsset(assetId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload?: {
      force?: boolean;
      mode?: 'missing' | 'table' | 'selected_columns' | 'all_columns';
      column_ids?: number[];
      batch_size?: number;
    }) =>
      customInstance<CatalogAIEnrichmentRunResponse>({
        url: `/api/v1/catalog/assets/${assetId}/ai-enrichment`,
        method: 'POST',
        data: {
          force: Boolean(payload?.force),
          ...(payload?.mode ? { mode: payload.mode } : {}),
          ...(payload?.column_ids ? { column_ids: payload.column_ids } : {}),
          ...(payload?.batch_size ? { batch_size: payload.batch_size } : {}),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.assets });
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.asset(assetId) });
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.overview });
    },
  });
}

export function useCatalogAIEnrichmentRun(runId?: number | null) {
  return useQuery({
    queryKey: catalogAppletKeys.aiEnrichmentRun(runId),
    queryFn: () =>
      customInstance<CatalogAIEnrichmentRunResponse>({
        url: `/api/v1/catalog/ai-enrichment/runs/${runId}`,
        method: 'GET',
      }),
    enabled: Boolean(runId),
    refetchInterval: (query) => {
      const data = query.state.data as CatalogAIEnrichmentRunResponse | undefined;
      return data?.status === 'pending' || data?.status === 'running' ? 1500 : false;
    },
  });
}

export function useUpdateCatalogWarehouseTable(assetId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CatalogTableMetadataUpdate) =>
      customInstance<CatalogWarehouseAsset>({
        url: `/api/v1/registry/tables/${assetId}`,
        method: 'PATCH',
        data: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.assets });
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.asset(assetId) });
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.overview });
    },
  });
}

export function useUpdateCatalogWarehouseColumn(assetId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ columnId, ...payload }: CatalogColumnMetadataUpdate) =>
      customInstance<CatalogColumnDetail>({
        url: `/api/v1/registry/columns/${columnId}`,
        method: 'PATCH',
        data: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.assets });
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.asset(assetId) });
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.overview });
    },
  });
}

export function useProfileTableSampleValues(assetId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload?: { force?: boolean; categorical?: boolean }) =>
      customInstance<ProfileSampleValuesResponse>({
        url: `/api/v1/registry/tables/${assetId}/profile-samples`,
        method: 'POST',
        data: {
          force: Boolean(payload?.force),
          ...(payload?.categorical !== undefined ? { categorical: payload.categorical } : {}),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.asset(assetId) });
    },
  });
}

export function useProfileConnectorSampleValues() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      connectorId,
      force,
      categorical,
    }: {
      connectorId: number;
      force?: boolean;
      categorical?: boolean;
    }) =>
      customInstance<ProfileConnectorSamplesResponse>({
        url: `/api/v1/registry/connectors/${connectorId}/profile-samples`,
        method: 'POST',
        data: {
          force: Boolean(force),
          ...(categorical !== undefined ? { categorical } : {}),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.assets });
    },
  });
}

export function useIndexTableValues(assetId?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload?: { force?: boolean }) =>
      customInstance<IndexValuesResponse>({
        url: `/api/v1/registry/tables/${assetId}/index-values`,
        method: 'POST',
        data: {
          force: Boolean(payload?.force),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.asset(assetId) });
    },
  });
}

export function useIndexConnectorValues() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ connectorId, force }: { connectorId: number; force?: boolean }) =>
      customInstance<IndexConnectorValuesResponse>({
        url: `/api/v1/registry/connectors/${connectorId}/index-values`,
        method: 'POST',
        data: {
          force: Boolean(force),
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.assets });
    },
  });
}

export function useCatalogProviders(enabled = true) {
  return useQuery({
    queryKey: catalogAppletKeys.providers,
    queryFn: () => customInstance<CatalogProviderSummary[]>({ url: '/api/v1/catalog/providers', method: 'GET' }),
    enabled,
  });
}

export function useCatalogGlossary(enabled = true) {
  return useQuery({
    queryKey: catalogAppletKeys.glossary,
    queryFn: () => customInstance<CatalogGlossaryTerm[]>({ url: '/api/v1/catalog/glossary', method: 'GET' }),
    enabled,
  });
}

export function usePendingCatalogCorrections(enabled = true) {
  return useQuery({
    queryKey: catalogAppletKeys.corrections,
    queryFn: () => customInstance<CatalogCorrection[]>({ url: '/api/v1/catalog/feedback/corrections/pending', method: 'GET' }),
    enabled,
  });
}

export function useCreateCatalogCorrection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CatalogCorrectionCreate) =>
      customInstance<CatalogCorrection>({
        url: '/api/v1/catalog/feedback/corrections',
        method: 'POST',
        data: payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.corrections });
      queryClient.invalidateQueries({ queryKey: catalogAppletKeys.overview });
    },
  });
}
